import React from "react"

import { sanitizeHtml } from "@/lib/sanitizeHtml"
import { getSyncDictionary } from "@/lib/sync-dictionary"
import { cn, GetExcerpt } from "@/lib/utils"
import { Link, Tag, Typography, type TagProps } from "@/components/ui/atoms"
import { Picture } from "@/components/ui/molecules"

type TCardNewsProps = {
  tagIcon?: TagProps["icon"]
  tagLabel: TagProps["label"]
  alt: string
  src: string
  title: string
  date?: string
  readTime: string
  type?: string
  description: string
  isDossier?: boolean
  link: string
  className?: string
  isH3?: boolean
  preview?: boolean
}

const CardNews = ({
  title,
  date,
  readTime,
  description,
  isDossier = false,
  link,
  className,
  type,
  src,
  alt,
  tagIcon,
  tagLabel,
  isH3 = true,
  preview,
}: TCardNewsProps) => {
  const excerptTitle = GetExcerpt(title, 70)
  const dictionary = getSyncDictionary().page.news

  if (preview !== true) {
    if (!title || !link || !src || !date || !readTime || !description) {
      return null
    }
  }

  return (
    <article
      className={cn(
        "card-news flex h-full flex-col items-stretch lg:col-span-2",
        className
      )}
    >
      {src ? (
        <Picture
          src={src}
          alt={alt}
          variant={"vertical"}
          tagLabel={tagLabel}
          tagIcon={tagIcon}
          alignLeft={true}
          className="shrink-0"
          isRounded={true}
        />
      ) : null}
      <div className="flex h-full flex-col justify-between pt-7">
        <div className="h-full">
          <header>
            <Typography
              as={isH3 ? "h3" : "h2"}
              variant="headingS"
              className="pb-5"
            >
              {excerptTitle}
            </Typography>
          </header>
          <Typography
            as="section"
            variant="paragraphS"
            className="line-clamp-3 text-neutral-400"
          >
            <span
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
            ></span>
          </Typography>
        </div>

        <footer className="flex flex-wrap items-baseline justify-between pt-5">
          <div className="flex items-baseline">
            {date && type === "news" ? (
              <p className="pb-2 pr-5">
                <Tag variant="ghost" label={date} />
              </p>
            ) : null}
            {date && type === "news" && readTime ? (
              <div className="pr-5">·</div>
            ) : (
              ""
            )}
            <Tag variant="ghost" label={readTime} />
          </div>
          <div className="flex w-full justify-end text-right md:w-auto">
            <Link
              chapters={[
                title,
                isDossier ? dictionary.readFolder : dictionary.readArticle,
              ]}
              title={title}
              variant="ghost"
              iconRight="ArrowRight"
              className="self-end p-0 font-500"
              href={link}
            >
              {isDossier ? dictionary.readFolder : dictionary.readArticle}
            </Link>
          </div>
        </footer>
      </div>
    </article>
  )
}

export { CardNews, type TCardNewsProps }
